import {
  TextField,
  Input,
  ValidationResult,
  FieldError,
} from "react-aria-components";
import { ChangeEvent } from "react";
import clsx from "clsx";

import Label from "./label";
import styles from "./text-input.module.css";

interface BaseInputProps {
  id?: string;
  "aria-label"?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  autocomplete?: string;
  autofocus?: boolean;
  className?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  isRequired?: boolean;
  label?: string;
  name: string;
  placeholder?: string;
  type?: "password" | "text" | "number";
  value?: string;
  onChange?: (ev: ChangeEvent<HTMLInputElement>) => void;
  validate?: (value: string) => string | null;
}

interface NumberInputProps extends BaseInputProps {
  type: "number";
  min?: string;
  max?: string;
  step?: string;
}

interface PasswordInputProps extends BaseInputProps {
  type: "password";
}

// Optional type because this is the default
interface TextInputTypeProps extends BaseInputProps {
  type?: "text";
  maxLength?: number;
}

type TextInputProps =
  TextInputTypeProps | PasswordInputProps | NumberInputProps;

function TextInput(props: TextInputProps) {
  const {
    id,
    autocomplete = "off",
    label,
    name,
    isDisabled = false,
    isRequired = false,
    autofocus = false,
    placeholder = "",
    type = "text",
    value,
    className,
    onChange,
    validate,
    errorMessage,
    isReadOnly,
    "aria-label": ariaLabel,
  } = props;
  const isInvalid = errorMessage ? { isInvalid: true } : {};
  const numberInputProps =
    props.type === "number"
      ? { min: props.min, max: props.max, step: props.step }
      : {};

  const maxLength =
    props.type === "text" ? { maxLength: props.maxLength } : undefined;
  return (
    <TextField
      id={id}
      {...maxLength}
      isReadOnly={isReadOnly}
      aria-label={ariaLabel}
      isDisabled={isDisabled}
      {...isInvalid}
      validate={validate}
      className={clsx(styles.textField, className)}
      name={name}
      isRequired={isRequired}
    >
      <div className={styles.labelRow}>
        {label && <Label>{label}</Label>}
        {isRequired && <div className={styles.required}>*</div>}
      </div>
      <Input
        value={value}
        {...numberInputProps}
        autoComplete={autocomplete}
        type={type}
        placeholder={placeholder}
        autoFocus={autofocus}
        className={clsx(styles.input, { [styles.readOnly]: isReadOnly })}
        onChange={onChange}
      />
      <FieldError className={styles.error}>{errorMessage}</FieldError>
    </TextField>
  );
}

export default TextInput;
